import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, AlertTriangle, DollarSign, UserCheck, Plus, X, Loader, RefreshCw, UserPlus, FileText, FolderOpen, Upload, Briefcase, CalendarDays, Clock, Download, Eye } from 'lucide-react';
import { GlassCard, GlassButton, GlassInput, FadeIn } from '../../components/ui';
import { downloadDocument, previewDocument } from '../../utils/documentViewer';

const API = '/erp/api/v1';

async function getToken() {
  const token = localStorage.getItem('nexus_token');
  if (!token) {
    window.location.assign('/erp/login');
    return '';
  }
  return token;
}

async function authHeaders() {
  const token = await getToken();
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function authOnlyHeaders() {
  const token = await getToken();
  return { Authorization: `Bearer ${token}` };
}

const TIPOS = ['salario', 'vale', 'rescisao', 'bonus'];
const FORMAS = ['dinheiro', 'pix', 'ted'];

const emptyLanc = {
  funcionario_id: '',
  tipo: 'salario',
  valor: '',
  data_pagamento: new Date().toISOString().split('T')[0],
  mes_referencia: new Date().toISOString().slice(0, 7),
  observacao: '',
  forma_pagamento: 'dinheiro',
};

const emptyFuncionario = {
  nome: '',
  funcao: '',
  empresa: 'MMC',
  regime: 'pendente_documentacao',
  status: 'ativo',
  salario_base: '',
  data_admissao: new Date().toISOString().split('T')[0],
  telefone: '',
  cpf: '',
};

const regimeMeta = (regime) => ({
  registrado: { label: 'Vínculo registrado', tone: 'bg-green-500/20 text-green-300' },
  pendente_documentacao: { label: 'Documentação do vínculo pendente', tone: 'bg-amber-500/20 text-amber-200' },
  informal: { label: 'Documentação do vínculo pendente', tone: 'bg-amber-500/20 text-amber-200' },
  autonomo: { label: 'Autônomo / validar documentação', tone: 'bg-blue-500/20 text-blue-200' },
  pj: { label: 'Prestador PJ', tone: 'bg-blue-500/20 text-blue-200' },
}[regime] || { label: regime || 'Não informado', tone: 'bg-white/10 text-white/70' });

const emptyDocForm = {
  tipo: 'documento',
  data_documento: new Date().toISOString().split('T')[0],
  observacao: '',
  file: null,
};

const emptyObraForm = {
  obra_id: '',
  etapa_id: '',
  funcao_obra: '',
  data_inicio: new Date().toISOString().split('T')[0],
  data_fim: '',
  status: 'ativo',
  observacao: '',
};

const emptyFeriasForm = {
  periodo_aquisitivo_inicio: '',
  periodo_aquisitivo_fim: '',
  dias_direito: '30',
  dias_gozados: '0',
  data_inicio_gozo: '',
  data_fim_gozo: '',
  status: 'aberta',
  observacao: '',
};

const emptyEncargoForm = {
  tipo: 'INSS',
  modo: 'percentual',
  valor: '',
  base: 'salario_base',
  observacao: '',
};

const emptyPontoForm = {
  data: new Date().toISOString().split('T')[0],
  tipo_dia: 'trabalhado',
  horas: '8',
  obra_id: '',
  etapa_id: '',
  observacao: '',
};

const emptyEmpreiteiraForm = {
  nome: '',
  razao_social: '',
  cnpj: '',
  responsavel: '',
  telefone: '',
  email: '',
  observacao: '',
};

const emptyContratoForm = {
  empreiteira_id: '',
  obra_id: '',
  numero_contrato: '',
  objeto: '',
  cno: '',
  data_inicio: new Date().toISOString().split('T')[0],
  data_fim: '',
  valor_contrato: '',
  retencao_percentual: '',
  observacao: '',
};

const emptyTerceiroForm = {
  nome: '',
  cpf: '',
  funcao: '',
  salario_carteira: '',
  data_inicio: new Date().toISOString().split('T')[0],
  observacao: '',
};

const emptyEmpDocForm = {
  tipo: 'comprovante_salario',
  competencia: new Date().toISOString().slice(0, 7),
  trabalhador_id: '',
  valor_deposito: '',
  data_documento: new Date().toISOString().split('T')[0],
  observacao: '',
  file: null,
};

const emptyMedicaoForm = {
  competencia: new Date().toISOString().slice(0, 7),
  etapa_id: '',
  periodo_inicio: '',
  periodo_fim: '',
  descricao: '',
  percentual_executado: '',
  valor_bruto: '',
  retencao_valor: '',
  observacao: '',
};

const openDocumentosOCR = (tipo = 'funcionario_documento') => {
  const params = new URLSearchParams({ acao: 'importar', modulo: 'rh', tipo });
  window.location.assign(`/erp/documentos?${params.toString()}`);
};

export const RH = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('funcionarios');
  const [empresa, setEmpresa] = useState('');
  const [mes, setMes] = useState(new Date().toISOString().slice(0, 7));

  const [funcionarios, setFuncionarios] = useState([]);
  const [lancamentos, setLancamentos] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [folhaOperacional, setFolhaOperacional] = useState(null);
  const [fechamentoMensal, setFechamentoMensal] = useState(null);
  const [dossie, setDossie] = useState(null);
  const [selectedFuncionarioId, setSelectedFuncionarioId] = useState('');
  const [obras, setObras] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [empreiteiras, setEmpreiteiras] = useState([]);
  const [contratosEmp, setContratosEmp] = useState([]);
  const [selectedContratoId, setSelectedContratoId] = useState('');
  const [contratoDossie, setContratoDossie] = useState(null);

  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showFuncionarioModal, setShowFuncionarioModal] = useState(false);
  const [dossieModal, setDossieModal] = useState('');
  const [empreiteiraModal, setEmpreiteiraModal] = useState('');
  const [form, setForm] = useState(emptyLanc);
  const [funcForm, setFuncForm] = useState(emptyFuncionario);
  const [docForm, setDocForm] = useState(emptyDocForm);
  const [obraForm, setObraForm] = useState(emptyObraForm);
  const [feriasForm, setFeriasForm] = useState(emptyFeriasForm);
  const [encargoForm, setEncargoForm] = useState(emptyEncargoForm);
  const [pontoForm, setPontoForm] = useState(emptyPontoForm);
  const [empreiteiraForm, setEmpreiteiraForm] = useState(emptyEmpreiteiraForm);
  const [contratoForm, setContratoForm] = useState(emptyContratoForm);
  const [terceiroForm, setTerceiroForm] = useState(emptyTerceiroForm);
  const [empDocForm, setEmpDocForm] = useState(emptyEmpDocForm);
  const [medicaoForm, setMedicaoForm] = useState(emptyMedicaoForm);
  const [medicaoContaPagar, setMedicaoContaPagar] = useState(null);
  const [vencimentoMedicao, setVencimentoMedicao] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const eParam = empresa ? `&empresa=${empresa}` : '';

      const [fRes, lRes, rRes, dRes, foRes, fmRes] = await Promise.all([
        fetch(`${API}/rh/funcionarios?per_page=100${eParam}`, { headers }),
        fetch(`${API}/rh/lancamentos?mes=${mes}${eParam}`, { headers }),
        fetch(`${API}/rh/lancamentos/resumo/${mes}${empresa ? `?empresa=${empresa}` : ''}`, { headers }),
        fetch(`${API}/rh/dashboard?mes=${mes}${empresa ? `&empresa=${empresa}` : ''}`, { headers }),
        fetch(`${API}/rh/folha-operacional/${mes}${empresa ? `?empresa=${empresa}` : ''}`, { headers }),
        fetch(`${API}/rh/fechamento-mensal/${mes}${empresa ? `?empresa=${empresa}` : ''}`, { headers }),
      ]);

      if (fRes.ok) setFuncionarios((await fRes.json()).items || []);
      if (lRes.ok) setLancamentos((await lRes.json()).items || []);
      if (rRes.ok) setResumo(await rRes.json());
      if (dRes.ok) setDashboard(await dRes.json());
      if (foRes.ok) setFolhaOperacional(await foRes.json());
      if (fmRes.ok) setFechamentoMensal(await fmRes.json());
    } catch (e) {
      console.error('[RH]', e);
      setError('Erro ao carregar dados');
    }
    setLoading(false);
  }, [empresa, mes]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const action = searchParams.get('acao');
    if (!action) return;
    const funcionarioTerm = (searchParams.get('funcionario') || '').toLowerCase();
    const funcionario = funcionarioTerm
      ? funcionarios.find((item) => (item.nome || '').toLowerCase().includes(funcionarioTerm))
      : null;
    if (funcionario) setSelectedFuncionarioId(funcionario.id);
    const dataParam = searchParams.get('data') || new Date().toISOString().split('T')[0];
    const observacao = searchParams.get('observacao') || '';
    const tipoParam = searchParams.get('tipo') || '';
    const handlers = {
      funcionario: () => { setActiveTab('funcionarios'); setFuncForm(emptyFuncionario); setShowFuncionarioModal(true); },
      lancamento: () => { setActiveTab('lancamentos'); setForm(emptyLanc); setShowModal(true); },
      ponto: () => { setActiveTab('dossie'); setPontoForm({ ...emptyPontoForm, data: dataParam, observacao }); setDossieModal('ponto'); },
      atestado: () => { setActiveTab('dossie'); setDocForm({ ...emptyDocForm, tipo: 'atestado', data_documento: dataParam, observacao }); setDossieModal('documento'); },
      documento: () => { setActiveTab('dossie'); setDocForm({ ...emptyDocForm, tipo: tipoParam || emptyDocForm.tipo, data_documento: dataParam, observacao }); setDossieModal('documento'); },
      ferias: () => { setActiveTab('dossie'); setFeriasForm(emptyFeriasForm); setDossieModal('ferias'); },
      empreiteira: () => { setActiveTab('empreiteiras'); setEmpreiteiraForm(emptyEmpreiteiraForm); setEmpreiteiraModal('empreiteira'); },
      empreiteira_documento: () => { setActiveTab('empreiteiras'); setEmpDocForm({ ...emptyEmpDocForm, tipo: tipoParam || emptyEmpDocForm.tipo, data_documento: dataParam, valor_deposito: searchParams.get('valor') || '', observacao }); setEmpreiteiraModal('documento'); },
      contrato_empreiteira: () => { setActiveTab('empreiteiras'); setContratoForm(emptyContratoForm); setEmpreiteiraModal('contrato'); },
    };
    if (handlers[action]) handlers[action]();
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, funcionarios]);

  const fetchDossie = useCallback(async (funcId = selectedFuncionarioId) => {
    if (!funcId) {
      setDossie(null);
      return;
    }
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/rh/funcionarios/${funcId}/dossie`, { headers });
      if (res.ok) setDossie(await res.json());
    } catch (e) {
      console.error('[RH] dossie', e);
    }
  }, [selectedFuncionarioId]);

  useEffect(() => {
    if (!selectedFuncionarioId && funcionarios.length > 0) {
      setSelectedFuncionarioId(funcionarios[0].id);
    }
  }, [funcionarios, selectedFuncionarioId]);

  useEffect(() => { fetchDossie(); }, [fetchDossie]);

  const fetchObras = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/construtora/obras?per_page=200`, { headers });
      if (res.ok) setObras((await res.json()).items || []);
    } catch (e) {
      console.error('[RH] obras', e);
    }
  }, []);

  useEffect(() => { fetchObras(); }, [fetchObras]);

  const fetchEtapas = useCallback(async (obraId) => {
    if (!obraId) {
      setEtapas([]);
      return;
    }
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/construtora/obras/${obraId}/etapas`, { headers });
      if (res.ok) setEtapas((await res.json()).etapas || []);
    } catch (e) {
      console.error('[RH] etapas', e);
    }
  }, []);

  useEffect(() => { fetchEtapas(obraForm.obra_id || pontoForm.obra_id); }, [fetchEtapas, obraForm.obra_id, pontoForm.obra_id]);

  const fetchEmpreiteiras = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const [eRes, cRes] = await Promise.all([
        fetch(`${API}/rh/empreiteiras`, { headers }),
        fetch(`${API}/rh/empreiteiras/contratos`, { headers }),
      ]);
      if (eRes.ok) setEmpreiteiras((await eRes.json()).items || []);
      if (cRes.ok) {
        const items = (await cRes.json()).items || [];
        setContratosEmp(items);
        if (!selectedContratoId && items.length > 0) setSelectedContratoId(items[0].id);
      }
    } catch (e) {
      console.error('[RH] empreiteiras', e);
    }
  }, [selectedContratoId]);

  useEffect(() => { fetchEmpreiteiras(); }, [fetchEmpreiteiras]);

  const fetchContratoDossie = useCallback(async (contratoId = selectedContratoId) => {
    if (!contratoId) {
      setContratoDossie(null);
      return;
    }
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/rh/empreiteiras/contratos/${contratoId}/dossie?competencia=${mes}`, { headers });
      if (res.ok) setContratoDossie(await res.json());
    } catch (e) {
      console.error('[RH] dossie empreiteira', e);
    }
  }, [selectedContratoId, mes]);

  useEffect(() => { fetchContratoDossie(); }, [fetchContratoDossie]);

  const closeDossieModal = () => {
    setDossieModal('');
    setError('');
  };

  const openDossieModal = (modal) => {
    setError('');
    setDossieModal(modal);
  };

  const closeEmpreiteiraModal = () => {
    setEmpreiteiraModal('');
    setMedicaoContaPagar(null);
    setVencimentoMedicao('');
    setError('');
  };

  const openEmpreiteiraModal = (modal) => {
    setError('');
    if (modal === 'medicao') {
      setMedicaoForm(emptyMedicaoForm);
      fetchEtapas(contratoDossie?.contrato?.obra_id);
    }
    setEmpreiteiraModal(modal);
  };

  const saveLancamento = async () => {
    if (!form.funcionario_id || !form.valor) {
      setError('Funcionário e valor são obrigatórios');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/rh/lancamentos`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...form, valor: parseFloat(form.valor) }),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erro ao salvar');
      } else {
        setShowModal(false);
        setForm(emptyLanc);
        fetchAll();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const saveFuncionario = async () => {
    if (!funcForm.nome || !funcForm.data_admissao) {
      setError('Nome e admissão são obrigatórios');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const body = {
        ...funcForm,
        salario_base: funcForm.salario_base ? parseFloat(funcForm.salario_base) : null,
      };
      const res = await fetch(`${API}/rh/funcionarios${funcForm.id ? `/${funcForm.id}` : ''}`, {
        method: funcForm.id ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erro ao salvar funcionário');
      } else {
        setShowFuncionarioModal(false);
        setFuncForm(emptyFuncionario);
        fetchAll();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const openFuncionarioModal = (funcionario = null) => {
    setError('');
    setFuncForm(funcionario ? {
      ...emptyFuncionario,
      ...funcionario,
      salario_base: funcionario.salario_base ?? '',
    } : emptyFuncionario);
    setShowFuncionarioModal(true);
  };

  const gerarFolhaOperacional = async () => {
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/rh/folhas/gerar-operacional`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ mes, empresa: empresa || undefined }),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erro ao gerar folha');
      } else {
        fetchAll();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const downloadFechamentoMensal = async () => {
    setError('');
    try {
      const headers = await authOnlyHeaders();
      const query = empresa ? `?empresa=${encodeURIComponent(empresa)}` : '';
      const response = await fetch(`${API}/rh/fechamento-mensal/${mes}/exportar.csv${query}`, { headers });
      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Erro ao gerar relatório de conferência');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `fechamento-rh-${mes}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Erro de conexão');
    }
  };

  const refreshDossieAfterSave = async () => {
    await fetchDossie();
    setSaving(false);
  };

  const saveDocumento = async () => {
    if (!selectedFuncionarioId || !docForm.file) {
      setError('Arquivo é obrigatório');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authOnlyHeaders();
      const fd = new FormData();
      fd.append('file', docForm.file);
      fd.append('tipo', docForm.tipo);
      fd.append('data_documento', docForm.data_documento || '');
      fd.append('observacao', docForm.observacao || '');
      const url = docForm.tipo === 'cartao_ponto'
        ? `${API}/rh/funcionarios/${selectedFuncionarioId}/cartao-ponto/importar`
        : `${API}/rh/funcionarios/${selectedFuncionarioId}/documentos`;
      const res = await fetch(url, { method: 'POST', headers, body: fd });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erro ao anexar documento');
      } else {
        closeDossieModal();
        setDocForm(emptyDocForm);
        await refreshDossieAfterSave();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const funcionarioDocUrl = (id) => `${API}/rh/funcionarios/documentos/${id}/download`;
  const empreiteiraDocUrl = (id) => `${API}/rh/empreiteiras/documentos/${id}/download`;
  const docName = (doc) => doc?.nome_armazenado || doc?.nome_original || 'documento';

  const previewDoc = async (url, doc) => {
    try {
      const headers = await authOnlyHeaders();
      await previewDocument(url, docName(doc), headers);
    } catch (err) {
      setError(err.message || 'Erro ao abrir documento');
    }
  };

  const downloadDoc = async (url, doc) => {
    try {
      const headers = await authOnlyHeaders();
      await downloadDocument(url, docName(doc), headers);
    } catch (err) {
      setError(err.message || 'Erro ao baixar documento');
    }
  };

  const saveObraVinculo = async () => {
    if (!selectedFuncionarioId || !obraForm.obra_id) {
      setError('Obra é obrigatória');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/rh/funcionarios/${selectedFuncionarioId}/obras`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...obraForm, etapa_id: obraForm.etapa_id || null, data_fim: obraForm.data_fim || null }),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erro ao vincular obra');
      } else {
        closeDossieModal();
        setObraForm(emptyObraForm);
        await refreshDossieAfterSave();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const saveFerias = async () => {
    if (!selectedFuncionarioId || !feriasForm.periodo_aquisitivo_inicio || !feriasForm.periodo_aquisitivo_fim) {
      setError('Período aquisitivo é obrigatório');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const body = {
        ...feriasForm,
        dias_direito: parseInt(feriasForm.dias_direito || '30', 10),
        dias_gozados: parseInt(feriasForm.dias_gozados || '0', 10),
        data_inicio_gozo: feriasForm.data_inicio_gozo || null,
        data_fim_gozo: feriasForm.data_fim_gozo || null,
      };
      const res = await fetch(`${API}/rh/funcionarios/${selectedFuncionarioId}/ferias`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erro ao registrar férias');
      } else {
        closeDossieModal();
        setFeriasForm(emptyFeriasForm);
        await refreshDossieAfterSave();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const saveEncargo = async () => {
    if (!selectedFuncionarioId || !encargoForm.tipo || !encargoForm.valor) {
      setError('Tipo e valor são obrigatórios');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/rh/funcionarios/${selectedFuncionarioId}/encargos`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...encargoForm, valor: parseFloat(encargoForm.valor) }),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erro ao registrar encargo');
      } else {
        closeDossieModal();
        setEncargoForm(emptyEncargoForm);
        await refreshDossieAfterSave();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const savePonto = async () => {
    if (!selectedFuncionarioId || !pontoForm.data) {
      setError('Data é obrigatória');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const body = {
        ...pontoForm,
        horas: parseFloat(pontoForm.horas || '0'),
        obra_id: pontoForm.obra_id || null,
        etapa_id: pontoForm.etapa_id || null,
      };
      const res = await fetch(`${API}/rh/funcionarios/${selectedFuncionarioId}/pontos`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erro ao registrar ponto');
      } else {
        closeDossieModal();
        setPontoForm(emptyPontoForm);
        await refreshDossieAfterSave();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const refreshEmpreiteiraAfterSave = async () => {
    await fetchEmpreiteiras();
    await fetchContratoDossie();
    setSaving(false);
  };

  const saveEmpreiteira = async () => {
    if (!empreiteiraForm.nome) {
      setError('Nome da empreiteira é obrigatório');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/rh/empreiteiras`, {
        method: 'POST',
        headers,
        body: JSON.stringify(empreiteiraForm),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erro ao salvar empreiteira');
      } else {
        closeEmpreiteiraModal();
        setEmpreiteiraForm(emptyEmpreiteiraForm);
        await refreshEmpreiteiraAfterSave();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const saveContratoEmpreiteira = async () => {
    if (!contratoForm.empreiteira_id || !contratoForm.obra_id) {
      setError('Empreiteira e obra são obrigatórias');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const body = {
        ...contratoForm,
        data_fim: contratoForm.data_fim || null,
        valor_contrato: contratoForm.valor_contrato ? parseFloat(contratoForm.valor_contrato) : null,
        retencao_percentual: contratoForm.retencao_percentual ? parseFloat(contratoForm.retencao_percentual) : null,
      };
      const res = await fetch(`${API}/rh/empreiteiras/contratos`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erro ao salvar contrato');
      } else {
        const saved = await res.json();
        setSelectedContratoId(saved.id);
        closeEmpreiteiraModal();
        setContratoForm(emptyContratoForm);
        await refreshEmpreiteiraAfterSave();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const saveTerceiro = async () => {
    if (!selectedContratoId || !terceiroForm.nome) {
      setError('Contrato e nome são obrigatórios');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const body = {
        ...terceiroForm,
        salario_carteira: terceiroForm.salario_carteira ? parseFloat(terceiroForm.salario_carteira) : null,
      };
      const res = await fetch(`${API}/rh/empreiteiras/contratos/${selectedContratoId}/trabalhadores`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erro ao salvar trabalhador');
      } else {
        closeEmpreiteiraModal();
        setTerceiroForm(emptyTerceiroForm);
        await refreshEmpreiteiraAfterSave();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const saveEmpDocumento = async () => {
    if (!selectedContratoId || !empDocForm.file || !empDocForm.tipo) {
      setError('Tipo e arquivo são obrigatórios');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authOnlyHeaders();
      const fd = new FormData();
      fd.append('file', empDocForm.file);
      fd.append('tipo', empDocForm.tipo);
      fd.append('competencia', empDocForm.competencia || '');
      fd.append('trabalhador_id', empDocForm.trabalhador_id || '');
      fd.append('valor_deposito', empDocForm.valor_deposito || '');
      fd.append('data_documento', empDocForm.data_documento || '');
      fd.append('observacao', empDocForm.observacao || '');
      const res = await fetch(`${API}/rh/empreiteiras/contratos/${selectedContratoId}/documentos`, { method: 'POST', headers, body: fd });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Erro ao anexar documento');
      } else {
        closeEmpreiteiraModal();
        setEmpDocForm(emptyEmpDocForm);
        await refreshEmpreiteiraAfterSave();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const saveMedicao = async () => {
    if (!selectedContratoId || !medicaoForm.competencia || !medicaoForm.descricao || !medicaoForm.valor_bruto) {
      setError('Competência, descrição e valor bruto são obrigatórios');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const body = {
        ...medicaoForm,
        etapa_id: medicaoForm.etapa_id || null,
        periodo_inicio: medicaoForm.periodo_inicio || null,
        periodo_fim: medicaoForm.periodo_fim || null,
        percentual_executado: medicaoForm.percentual_executado === '' ? null : Number(medicaoForm.percentual_executado),
        valor_bruto: Number(medicaoForm.valor_bruto),
        retencao_valor: Number(medicaoForm.retencao_valor || 0),
      };
      const res = await fetch(`${API}/rh/empreiteiras/contratos/${selectedContratoId}/medicoes`, { method: 'POST', headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Erro ao registrar medição');
      else {
        closeEmpreiteiraModal();
        setMedicaoForm(emptyMedicaoForm);
        await refreshEmpreiteiraAfterSave();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const approveMedicao = async (medicao) => {
    if (!window.confirm(`Aprovar a medição de ${fmtBRL(medicao.valor_bruto)}? Isto não criará conta a pagar.`)) return;
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/rh/empreiteiras/contratos/${selectedContratoId}/medicoes/${medicao.id}/aprovar`, { method: 'POST', headers, body: JSON.stringify({}) });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Erro ao aprovar medição');
      else await refreshEmpreiteiraAfterSave();
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const openMedicaoContaPagar = (medicao) => {
    setError('');
    setMedicaoContaPagar(medicao);
    setVencimentoMedicao('');
    setEmpreiteiraModal('conta-pagar-medicao');
  };

  const createMedicaoContaPagar = async () => {
    if (!medicaoContaPagar || !vencimentoMedicao) {
      setError('Informe o vencimento da conta a pagar');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/rh/empreiteiras/contratos/${selectedContratoId}/medicoes/${medicaoContaPagar.id}/gerar-conta-pagar`, { method: 'POST', headers, body: JSON.stringify({ data_vencimento: vencimentoMedicao }) });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Erro ao gerar conta a pagar');
      else {
        closeEmpreiteiraModal();
        await refreshEmpreiteiraAfterSave();
      }
    } catch (e) {
      setError('Erro de conexão');
    }
    setSaving(false);
  };

  const fmtBRL = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  const fmtDate = (s) => s ? new Date(s + 'T12:00:00').toLocaleDateString('pt-BR') : '-';

  const pendenciasVinculo = funcionarios.filter(f => ['informal', 'pendente_documentacao'].includes(f.regime) && f.status === 'ativo');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Recursos Humanos</h1>
          <p className="text-white/50">Gestão de pessoal e folha de pagamento</p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <a
            href={`${process.env.PUBLIC_URL || ''}/manuals/playbook-humano-rh.md`}
            download
            className="relative inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-accent-primary/50 bg-white/10 text-white/90 border border-white/20 hover:bg-white/20 hover:border-white/30 px-5 py-2.5 text-sm"
          >
            <Download className="w-4 h-4" />
            Manual
          </a>
          <select
            value={empresa}
            onChange={e => setEmpresa(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="">Todas as empresas</option>
            <option value="MMI">MMI</option>
            <option value="MMC">MMC</option>
          </select>
          <input
            type="month"
            value={mes}
            onChange={e => setMes(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
          />
          <button onClick={fetchAll} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => openDocumentosOCR('atestado')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 text-sm flex items-center gap-2">
            <Upload size={16} /> Atestado
          </button>
          <button onClick={() => openDocumentosOCR('cartao_ponto')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 text-sm flex items-center gap-2">
            <Upload size={16} /> Cartão ponto
          </button>
          <button onClick={() => openDocumentosOCR('empreiteira_documento')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 text-sm flex items-center gap-2">
            <Upload size={16} /> Empreiteira
          </button>
          <button onClick={() => openFuncionarioModal()} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 text-sm flex items-center gap-2">
            <UserPlus size={16} /> Funcionário
          </button>
          <GlassButton onClick={() => setShowModal(true)} className="flex items-center gap-2">
            <Plus size={16} /> Novo Lançamento
          </GlassButton>
        </div>
      </div>

      {/* KPIs */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <Users size={20} className="text-blue-400" />
              <div>
                <p className="text-white/50 text-xs">Total Funcionários</p>
                <p className="text-2xl font-bold text-white">{dashboard.total_funcionarios}</p>
              </div>
            </div>
          </GlassCard>
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <UserCheck size={20} className="text-green-400" />
              <div>
                <p className="text-white/50 text-xs">Ativos</p>
                <p className="text-2xl font-bold text-white">{dashboard.ativos}</p>
              </div>
            </div>
          </GlassCard>
          <GlassCard className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign size={20} className="text-yellow-400" />
              <div>
                <p className="text-white/50 text-xs">Custo {mes}</p>
                <p className="text-xl font-bold text-white">{fmtBRL(dashboard.custo_mes)}</p>
              </div>
            </div>
          </GlassCard>
          <GlassCard className={`p-4 ${(dashboard.pendencias_vinculo || dashboard.informais) > 0 ? 'border border-amber-400/50' : ''}`}>
            <div className="flex items-center gap-3">
              <AlertTriangle size={20} className={(dashboard.pendencias_vinculo || dashboard.informais) > 0 ? 'text-amber-300' : 'text-white/30'} />
              <div>
                <p className="text-white/50 text-xs">Vínculos a conferir</p>
                <p className={`text-2xl font-bold ${(dashboard.pendencias_vinculo || dashboard.informais) > 0 ? 'text-amber-300' : 'text-white'}`}>
                  {dashboard.pendencias_vinculo || dashboard.informais || 0}
                </p>
                {(dashboard.pendencias_vinculo || dashboard.informais) > 0 && <p className="text-amber-200/70 text-xs">não é parecer jurídico</p>}
              </div>
            </div>
          </GlassCard>
        </div>
      )}

      {pendenciasVinculo.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-400/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-amber-200 font-semibold mb-2">
            <AlertTriangle size={18} /> {pendenciasVinculo.length} vínculo(s) com documentação a conferir
          </div>
          <p className="mb-3 text-sm text-amber-100/75">Revise o tipo de vínculo e os documentos no dossiê. Trabalhadores de empreiteira devem ser cadastrados na aba Empreiteiras, não como funcionários próprios.</p>
          <div className="flex flex-wrap gap-2">
            {pendenciasVinculo.map(f => (
              <span key={f.id} className="bg-amber-500/20 text-amber-100 text-xs px-2 py-1 rounded-full">
                {f.nome} · {f.funcao}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Abas */}
      <div className="flex gap-2 border-b border-white/10">
        {[
          { id: 'funcionarios', label: 'Funcionários' },
          { id: 'lancamentos', label: 'Lançamentos' },
	          { id: 'resumo', label: 'Resumo Mensal' },
	          { id: 'folha', label: 'Folha Operacional' },
	          { id: 'fechamento', label: 'Pré-fechamento' },
	          { id: 'dossie', label: 'Dossiê' },
	          { id: 'empreiteiras', label: 'Empreiteiras' },
	        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm rounded-t-lg transition-all ${
              activeTab === tab.id
                ? 'bg-white/10 text-white border-b-2 border-blue-400'
                : 'text-white/50 hover:text-white/70'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader className="animate-spin text-white/50" /></div>
      ) : (
        <FadeIn>
          {/* TAB FUNCIONÁRIOS */}
          {activeTab === 'funcionarios' && (
            <GlassCard className="overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    {['Nome', 'Função', 'Empresa', 'Vínculo', 'Salário Base', 'Admissão', 'Ações'].map(h => (
                      <th key={h} className="text-left text-white/50 text-xs px-4 py-3 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {funcionarios.map(f => (
                    <tr key={f.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3 text-white text-sm">{f.nome}</td>
                      <td className="px-4 py-3 text-white/70 text-sm">{f.funcao || f.cargo || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          f.empresa === 'MMC' ? 'bg-orange-500/20 text-orange-300' : 'bg-blue-500/20 text-blue-300'
                        }`}>{f.empresa || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${regimeMeta(f.regime).tone}`}>{regimeMeta(f.regime).label}</span>
                      </td>
                      <td className="px-4 py-3 text-white/70 text-sm">{fmtBRL(f.salario_base)}</td>
                      <td className="px-4 py-3 text-white/50 text-sm">{fmtDate(f.data_admissao)}</td>
                      <td className="px-4 py-3"><button onClick={() => openFuncionarioModal(f)} className="text-xs text-blue-300 hover:text-blue-200">Editar</button></td>
                    </tr>
                  ))}
                  {funcionarios.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-white/30">Nenhum funcionário encontrado</td></tr>
                  )}
                </tbody>
              </table>
            </GlassCard>
          )}

          {/* TAB LANÇAMENTOS */}
          {activeTab === 'lancamentos' && (
            <GlassCard className="overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    {['Funcionário', 'Tipo', 'Valor', 'Pagamento', 'Forma', 'Empresa', 'Obs'].map(h => (
                      <th key={h} className="text-left text-white/50 text-xs px-4 py-3 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lancamentos.map(l => (
                    <tr key={l.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3">
                        <p className="text-white text-sm">{l.funcionario_nome}</p>
                        {['informal', 'pendente_documentacao'].includes(l.funcionario_regime) && (
                          <span className="text-amber-300 text-xs">vínculo a conferir</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                          l.tipo === 'salario' ? 'bg-blue-500/20 text-blue-300' :
                          l.tipo === 'vale' ? 'bg-yellow-500/20 text-yellow-300' :
                          l.tipo === 'rescisao' ? 'bg-red-500/20 text-red-300' :
                          'bg-green-500/20 text-green-300'
                        }`}>{l.tipo}</span>
                      </td>
                      <td className="px-4 py-3 text-white font-medium text-sm">{fmtBRL(l.valor)}</td>
                      <td className="px-4 py-3 text-white/70 text-sm">{fmtDate(l.data_pagamento)}</td>
                      <td className="px-4 py-3 text-white/50 text-sm capitalize">{l.forma_pagamento}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          l.empresa === 'MMC' ? 'bg-orange-500/20 text-orange-300' : 'bg-blue-500/20 text-blue-300'
                        }`}>{l.empresa}</span>
                      </td>
                      <td className="px-4 py-3 text-white/40 text-xs max-w-xs truncate">{l.observacao || '-'}</td>
                    </tr>
                  ))}
                  {lancamentos.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-white/30">Nenhum lançamento em {mes}</td></tr>
                  )}
                </tbody>
              </table>
            </GlassCard>
          )}

          {/* TAB RESUMO MENSAL */}
          {activeTab === 'resumo' && resumo && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <GlassCard className="p-5">
                <p className="text-white/50 text-sm mb-1">Total Folha {resumo.mes}</p>
                <p className="text-3xl font-bold text-white">{fmtBRL(resumo.total)}</p>
                <p className="text-white/40 text-xs mt-1">{resumo.total_lancamentos} lançamentos · {resumo.funcionarios_pagos} pessoas</p>
              </GlassCard>
              <GlassCard className="p-5">
                <p className="text-white/50 text-sm mb-3">Por tipo</p>
                <div className="space-y-2">
                  {Object.entries(resumo.por_tipo || {}).map(([tipo, valor]) => (
                    <div key={tipo} className="flex justify-between items-center">
                      <span className="text-white/70 text-sm capitalize">{tipo}</span>
                      <span className="text-white font-medium text-sm">{fmtBRL(valor)}</span>
                    </div>
                  ))}
                </div>
              </GlassCard>
              <GlassCard className="p-5">
                <p className="text-white/50 text-sm mb-3">Alertas</p>
                {(resumo.pendencias_vinculo_no_mes || resumo.informais_no_mes) > 0 ? (
                  <div className="flex items-center gap-2 text-amber-200">
                    <AlertTriangle size={18} />
                    <div>
                      <p className="font-semibold">{resumo.pendencias_vinculo_no_mes || resumo.informais_no_mes} vínculo(s) a conferir</p>
                      <p className="text-xs text-amber-100/70">revise cadastro e documentação; não é conclusão jurídica</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-green-400 text-sm">✓ Sem pendências de vínculo nos lançamentos</p>
                )}
              </GlassCard>
            </div>
          )}

          {/* TAB FOLHA OPERACIONAL */}
	          {activeTab === 'folha' && folhaOperacional && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <GlassCard className="p-5">
                  <p className="text-white/50 text-sm mb-1">Folha {folhaOperacional.mes}</p>
                  <p className="text-3xl font-bold text-white">{fmtBRL(folhaOperacional.total)}</p>
                </GlassCard>
                <GlassCard className="p-5">
                  <p className="text-white/50 text-sm mb-1">Pessoas Pagas</p>
                  <p className="text-3xl font-bold text-white">{folhaOperacional.funcionarios_pagos}</p>
                </GlassCard>
                <GlassCard className="p-5">
                  <p className="text-white/50 text-sm mb-1">Vínculos a conferir</p>
                  <p className={`text-3xl font-bold ${(folhaOperacional.pendencias_vinculo_no_mes || folhaOperacional.informais_no_mes) > 0 ? 'text-amber-300' : 'text-white'}`}>
                    {folhaOperacional.pendencias_vinculo_no_mes || folhaOperacional.informais_no_mes || 0}
                  </p>
                </GlassCard>
                <GlassCard className="p-5">
                  <button
                    onClick={gerarFolhaOperacional}
                    disabled={saving}
                    className="w-full h-full min-h-[72px] rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 text-sm flex items-center justify-center gap-2"
                  >
                    {saving ? <Loader size={16} className="animate-spin" /> : <FileText size={16} />}
                    Gerar Folha
                  </button>
                </GlassCard>
              </div>

              <GlassCard className="overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      {['Funcionário', 'Função', 'Regime', 'Empresa', 'Lançamentos', 'Total'].map(h => (
                        <th key={h} className="text-left text-white/50 text-xs px-4 py-3 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(folhaOperacional.funcionarios || []).map(row => (
                      <tr key={row.funcionario_id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="px-4 py-3 text-white text-sm">{row.funcionario_nome}</td>
                        <td className="px-4 py-3 text-white/60 text-sm">{row.funcao || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${regimeMeta(row.regime).tone}`}>{regimeMeta(row.regime).label}</span>
                        </td>
                        <td className="px-4 py-3 text-white/60 text-sm">{row.empresa}</td>
                        <td className="px-4 py-3 text-white/60 text-sm">{row.lancamentos}</td>
                        <td className="px-4 py-3 text-white font-medium text-sm">{fmtBRL(row.total)}</td>
                      </tr>
                    ))}
                    {(folhaOperacional.funcionarios || []).length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-white/30">Nenhum lançamento em {mes}</td></tr>
                    )}
                  </tbody>
                </table>
              </GlassCard>
            </div>
	          )}

	          {activeTab === 'fechamento' && fechamentoMensal && (
	            <div className="space-y-4">
	              <GlassCard className="border border-amber-400/25 bg-amber-500/10 p-5">
	                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between"><div className="flex items-start gap-3"><AlertTriangle className="mt-0.5 text-amber-200" size={20} /><div><p className="font-medium text-amber-100">Pré-fechamento operacional - {fechamentoMensal.competencia}</p><p className="mt-1 text-sm text-white/70">{fechamentoMensal.aviso}</p></div></div><button onClick={downloadFechamentoMensal} className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white/90 hover:bg-white/20"><Download size={16} /> Baixar relatório</button></div>
	              </GlassCard>
	              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
	                <GlassCard className="p-5"><p className="text-sm text-white/50">Folha lançada</p><p className="mt-1 text-2xl font-bold text-white">{fmtBRL(fechamentoMensal.folha?.total)}</p><p className="mt-1 text-xs text-white/40">{fechamentoMensal.folha?.total_lancamentos || 0} lançamento(s)</p></GlassCard>
	                <GlassCard className="p-5"><p className="text-sm text-white/50">Encargos configurados</p><p className="mt-1 text-2xl font-bold text-white">{fmtBRL(fechamentoMensal.encargos_estimados_configurados)}</p><p className="mt-1 text-xs text-white/40">Estimativa; validar com contador.</p></GlassCard>
	                <GlassCard className="p-5"><p className="text-sm text-white/50">Pontos revisados</p><p className="mt-1 text-2xl font-bold text-white">{fechamentoMensal.pontos_revisados || 0}</p><p className="mt-1 text-xs text-white/40">{fechamentoMensal.funcionarios_sem_ponto_revisado?.length || 0} sem ponto revisado</p></GlassCard>
	                <GlassCard className="p-5"><p className="text-sm text-white/50">Pendências empreiteira</p><p className="mt-1 text-2xl font-bold text-white">{fechamentoMensal.empreiteiras?.pendencias_documentais || 0}</p><p className="mt-1 text-xs text-white/40">{fechamentoMensal.empreiteiras?.contratos_ativos || 0} contrato(s) ativo(s)</p></GlassCard>
	              </div>
	              <GlassCard className="overflow-hidden"><div className="border-b border-white/10 px-5 py-4"><h3 className="font-semibold text-white">Checklist de conferência</h3><p className="mt-1 text-xs text-white/45">Use para organizar o mês antes de enviar informações ao contador.</p></div><div className="divide-y divide-white/5">{(fechamentoMensal.checks || []).map(item => <div key={item.id} className="flex flex-col gap-2 px-5 py-4 md:flex-row md:items-center md:justify-between"><div><p className="font-medium text-white/85">{item.titulo}</p><p className="mt-1 text-sm text-white/50">{item.detalhe}</p></div><span className={`text-xs font-medium ${item.status === 'ok' ? 'text-emerald-300' : item.status === 'atencao' ? 'text-rose-300' : 'text-amber-200'}`}>{item.status === 'ok' ? 'Conferido' : item.status === 'atencao' ? 'Atenção' : 'Revisar'}</span></div>)}</div></GlassCard>
	              {fechamentoMensal.ferias_vencidas?.length > 0 && <GlassCard className="p-5"><p className="font-medium text-rose-200">Férias vencidas</p><div className="mt-3 space-y-2 text-sm text-white/65">{fechamentoMensal.ferias_vencidas.map(item => <p key={item.id}>{item.funcionario_nome} - saldo de {item.dias_saldo} dia(s). Consulte o dossiê do funcionário para programar.</p>)}</div></GlassCard>}
	            </div>
	          )}

	          {/* TAB DOSSIÊ */}
	          {activeTab === 'dossie' && (
	            <div className="space-y-4">
              <GlassCard className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <FolderOpen size={18} className="text-blue-300" />
                  <select
                    value={selectedFuncionarioId}
                    onChange={e => setSelectedFuncionarioId(e.target.value)}
                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm min-w-[260px]"
                  >
                    {funcionarios.map(f => <option key={f.id} value={f.id}>{f.nome} — {f.funcao || f.cargo}</option>)}
                  </select>
	                  <button onClick={() => fetchDossie()} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm">
	                    Atualizar
	                  </button>
	                  {selectedFuncionarioId && (
	                    <div className="flex flex-wrap gap-2 ml-auto">
	                      <button onClick={() => openDossieModal('documento')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm flex items-center gap-2">
	                        <Upload size={14} /> Documento
	                      </button>
	                      <button onClick={() => openDossieModal('obra')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm flex items-center gap-2">
	                        <Briefcase size={14} /> Obra
	                      </button>
	                      <button onClick={() => openDossieModal('ferias')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm flex items-center gap-2">
	                        <CalendarDays size={14} /> Férias
	                      </button>
	                      <button onClick={() => openDossieModal('encargo')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm flex items-center gap-2">
	                        <FileText size={14} /> Encargo
	                      </button>
	                      <button onClick={() => openDossieModal('ponto')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm flex items-center gap-2">
	                        <Clock size={14} /> Ponto
	                      </button>
	                    </div>
	                  )}
	                </div>
	              </GlassCard>

              {dossie ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <GlassCard className="p-5">
                    <p className="text-white/50 text-sm mb-3">Ficha</p>
                    <h3 className="text-white font-semibold text-lg">{dossie.funcionario.nome}</h3>
                    <p className="text-white/60 text-sm">{dossie.funcionario.funcao || dossie.funcionario.cargo || '-'}</p>
                    <div className="mt-4 space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-white/40">Empresa</span><span className="text-white/80">{dossie.funcionario.empresa}</span></div>
                      <div className="flex justify-between"><span className="text-white/40">Regime</span><span className="text-white/80">{dossie.funcionario.regime}</span></div>
                      <div className="flex justify-between"><span className="text-white/40">Admissão</span><span className="text-white/80">{fmtDate(dossie.funcionario.data_admissao)}</span></div>
                      <div className="flex justify-between"><span className="text-white/40">Salário</span><span className="text-white/80">{fmtBRL(dossie.funcionario.salario_base)}</span></div>
                    </div>
                  </GlassCard>

                  <GlassCard className="p-5">
                    <p className="text-white/50 text-sm mb-3">Férias</p>
                    <p className={`text-2xl font-bold ${dossie.ferias.vencidas > 0 ? 'text-red-400' : 'text-white'}`}>
                      {dossie.ferias.vencidas > 0 ? `${dossie.ferias.vencidas} vencida(s)` : 'Em dia'}
                    </p>
                    <p className="text-white/40 text-xs mt-2">{dossie.ferias.observacao || `${(dossie.ferias.periodos || []).length} período(s) cadastrado(s)`}</p>
                  </GlassCard>

                  <GlassCard className="p-5">
                    <p className="text-white/50 text-sm mb-3">Encargos Configurados</p>
                    <p className="text-2xl font-bold text-white">{fmtBRL(dossie.encargos.total_estimado)}</p>
                    <div className="mt-3 space-y-2">
                      {(dossie.encargos.items || []).map(e => (
                        <div key={e.id} className="flex justify-between text-xs">
                          <span className="text-white/60">{e.tipo} · {e.modo}</span>
                          <span className="text-white/80">{fmtBRL(e.valor_estimado)}</span>
                        </div>
                      ))}
                      {(dossie.encargos.items || []).length === 0 && <p className="text-white/35 text-xs">Nenhum encargo configurado</p>}
                    </div>
                  </GlassCard>

                  <GlassCard className="p-5 lg:col-span-2">
                    <p className="text-white/50 text-sm mb-3">Documentos</p>
                    <div className="space-y-2">
	                      {(dossie.documentos || []).slice(0, 8).map(doc => (
	                        <div key={doc.id} className="flex items-center justify-between gap-3 border-b border-white/5 pb-2">
	                          <div>
	                            <p className="text-white/80 text-sm">{doc.nome_original}</p>
	                            <p className="text-white/40 text-xs">{doc.tipo} · {doc.status} · {fmtDate(doc.data_documento)}</p>
	                          </div>
	                          <div className="flex items-center gap-1">
	                            <span className="mr-2 text-white/30 text-xs">{Math.round((doc.tamanho_bytes || 0) / 1024)} KB</span>
	                            <button title="Abrir" onClick={() => previewDoc(funcionarioDocUrl(doc.id), doc)} className="rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white">
	                              <Eye className="h-4 w-4" />
	                            </button>
	                            <button title="Baixar" onClick={() => downloadDoc(funcionarioDocUrl(doc.id), doc)} className="rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white">
	                              <Download className="h-4 w-4" />
	                            </button>
	                          </div>
	                        </div>
	                      ))}
                      {(dossie.documentos || []).length === 0 && <p className="text-white/35 text-sm">Sem documentos anexados</p>}
                    </div>
                  </GlassCard>

                  <GlassCard className="p-5">
                    <p className="text-white/50 text-sm mb-3">Obras Vinculadas</p>
                    <div className="space-y-2">
                      {(dossie.alocacoes_obra || []).map(a => (
                        <div key={a.id} className="border-b border-white/5 pb-2">
                          <p className="text-white/80 text-sm">{a.obra}</p>
                          <p className="text-white/40 text-xs">{a.etapa || 'Sem etapa'} · {a.status}</p>
                        </div>
                      ))}
                      {(dossie.alocacoes_obra || []).length === 0 && <p className="text-white/35 text-sm">Sem vínculo de obra</p>}
                    </div>
                  </GlassCard>

                  <GlassCard className="overflow-hidden lg:col-span-3">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          {['Data', 'Tipo', 'Horas', 'Obra', 'Origem', 'Status'].map(h => (
                            <th key={h} className="text-left text-white/50 text-xs px-4 py-3 font-medium">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(dossie.pontos || []).slice(0, 20).map(p => (
                          <tr key={p.id} className="border-b border-white/5">
                            <td className="px-4 py-3 text-white/80 text-sm">{fmtDate(p.data)}</td>
                            <td className="px-4 py-3 text-white/70 text-sm">{p.tipo_dia}</td>
                            <td className="px-4 py-3 text-white/70 text-sm">{p.horas}</td>
                            <td className="px-4 py-3 text-white/50 text-sm">{p.obra || '-'}</td>
                            <td className="px-4 py-3 text-white/50 text-sm">{p.origem}</td>
                            <td className="px-4 py-3 text-white/50 text-sm">{p.status}</td>
                          </tr>
                        ))}
                        {(dossie.pontos || []).length === 0 && (
                          <tr><td colSpan={6} className="px-4 py-8 text-center text-white/30">Sem pontos registrados</td></tr>
                        )}
                      </tbody>
                    </table>
                  </GlassCard>
                </div>
              ) : (
                <GlassCard className="p-8 text-center text-white/35">Selecione um funcionário</GlassCard>
              )}
	            </div>
	          )}

	          {/* TAB EMPREITEIRAS */}
	          {activeTab === 'empreiteiras' && (
	            <div className="space-y-4">
	              <GlassCard className="p-4">
	                <div className="flex flex-wrap items-center gap-3">
	                  <Briefcase size={18} className="text-orange-300" />
	                  <select
	                    value={selectedContratoId}
	                    onChange={e => setSelectedContratoId(e.target.value)}
	                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm min-w-[300px]"
	                  >
	                    <option value="">Selecionar contrato...</option>
	                    {contratosEmp.map(c => <option key={c.id} value={c.id}>{c.empreiteira} · {c.obra} · CNO {c.cno || '-'}</option>)}
	                  </select>
	                  <button onClick={() => fetchContratoDossie()} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm">
	                    Atualizar
	                  </button>
	                  <div className="flex flex-wrap gap-2 ml-auto">
	                    <button onClick={() => openEmpreiteiraModal('empreiteira')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm flex items-center gap-2">
	                      <Plus size={14} /> Empreiteira
	                    </button>
	                    <button onClick={() => openEmpreiteiraModal('contrato')} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 text-sm flex items-center gap-2">
	                      <FileText size={14} /> Contrato
	                    </button>
	                    <button onClick={() => openEmpreiteiraModal('trabalhador')} disabled={!selectedContratoId} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white/70 text-sm flex items-center gap-2">
	                      <UserPlus size={14} /> Trabalhador
	                    </button>
	                    <button onClick={() => openEmpreiteiraModal('documento')} disabled={!selectedContratoId} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white/70 text-sm flex items-center gap-2">
	                      <Upload size={14} /> Documento
	                    </button>
	                    <button onClick={() => openEmpreiteiraModal('medicao')} disabled={!selectedContratoId} className="px-3 py-2 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 disabled:opacity-40 text-indigo-100 text-sm flex items-center gap-2">
	                      <FileText size={14} /> Medição
	                    </button>
	                  </div>
	                </div>
	              </GlassCard>

	              {contratoDossie ? (
	                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
	                  <GlassCard className="p-5">
	                    <p className="text-white/50 text-sm mb-3">Contrato</p>
	                    <h3 className="text-white font-semibold text-lg">{contratoDossie.empreiteira?.nome || contratoDossie.contrato.empreiteira}</h3>
	                    <p className="text-white/60 text-sm">{contratoDossie.contrato.obra}</p>
	                    <div className="mt-4 space-y-2 text-sm">
	                      <div className="flex justify-between"><span className="text-white/40">CNO</span><span className="text-white/80">{contratoDossie.contrato.cno || '-'}</span></div>
	                      <div className="flex justify-between"><span className="text-white/40">Objeto</span><span className="text-white/80 text-right">{contratoDossie.contrato.objeto || '-'}</span></div>
	                      <div className="flex justify-between"><span className="text-white/40">Valor</span><span className="text-white/80">{fmtBRL(contratoDossie.contrato.valor_contrato)}</span></div>
	                    </div>
	                  </GlassCard>

	                  <GlassCard className="p-5">
	                    <p className="text-white/50 text-sm mb-3">Medições</p>
	                    <div className="space-y-2 text-sm">
	                      <div className="flex justify-between"><span className="text-white/40">Contratado</span><span className="text-white/80">{fmtBRL(contratoDossie.resumo_medicoes?.valor_contratado)}</span></div>
	                      <div className="flex justify-between"><span className="text-white/40">Medido</span><span className="text-amber-200">{fmtBRL(contratoDossie.resumo_medicoes?.valor_medido)}</span></div>
	                      <div className="flex justify-between"><span className="text-white/40">Aprovado</span><span className="text-emerald-200">{fmtBRL(contratoDossie.resumo_medicoes?.valor_aprovado)}</span></div>
	                      <div className="flex justify-between"><span className="text-white/40">Título pendente</span><span className="text-amber-200">{fmtBRL(contratoDossie.resumo_medicoes?.valor_titulos_pendentes)}</span></div>
	                      <div className="flex justify-between"><span className="text-white/40">Pago no Financeiro</span><span className="text-emerald-200">{fmtBRL(contratoDossie.resumo_medicoes?.valor_pago_financeiro)}</span></div>
	                      <div className="flex justify-between"><span className="text-white/40">Saldo contratado</span><span className="text-white/80">{fmtBRL(contratoDossie.resumo_medicoes?.saldo_contrato)}</span></div>
	                    </div>
	                    <p className="mt-3 text-xs text-white/40">Aprovar uma medição não gera pagamento nem conta a pagar.</p>
	                  </GlassCard>

	                  <GlassCard className={`p-5 ${contratoDossie.compliance.total_pendentes > 0 ? 'border border-yellow-500/40' : ''}`}>
	                    <p className="text-white/50 text-sm mb-3">Conformidade {mes}</p>
	                    <p className={`text-2xl font-bold ${contratoDossie.compliance.total_pendentes > 0 ? 'text-yellow-300' : 'text-green-300'}`}>
	                      {contratoDossie.compliance.total_pendentes} pendência(s)
	                    </p>
	                    <p className="text-white/40 text-xs mt-2">{contratoDossie.compliance.total_obrigatorios} itens obrigatórios monitorados</p>
	                    {(contratoDossie.compliance.mensais_pendentes || []).length > 0 && (
	                      <p className="text-yellow-200/70 text-xs mt-2">Mensais: {contratoDossie.compliance.mensais_pendentes.join(', ')}</p>
	                    )}
	                  </GlassCard>

	                  <GlassCard className="p-5">
	                    <p className="text-white/50 text-sm mb-3">Obrigatórios</p>
	                    <div className="space-y-2 text-xs text-white/60">
	                      <p>Mensal: salário, FGTS, GFIP, DDS</p>
	                      <p>Por trabalhador: registro, NRs, exame médico</p>
	                      <p>Depósitos devem bater com salário em carteira.</p>
	                    </div>
	                  </GlassCard>

	                  <GlassCard className="overflow-hidden lg:col-span-3">
	                    <div className="border-b border-white/10 px-5 py-4"><h3 className="text-white font-semibold">Medições de serviço</h3><p className="mt-1 text-xs text-white/45">Registre o avanço medido, aprove depois da conferência e siga com o Financeiro somente quando for devido.</p></div>
	                    <div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b border-white/10">{['Competência', 'Etapa', 'Descrição', 'Bruto', 'Retenção', 'Líquido', 'Medição', 'Financeiro', 'Ação'].map(h => <th key={h} className="text-left text-white/50 text-xs px-4 py-3 font-medium">{h}</th>)}</tr></thead><tbody>
	                      {(contratoDossie.medicoes || []).map(m => <tr key={m.id} className="border-b border-white/5"><td className="px-4 py-3 text-white/70 text-sm">{m.competencia}</td><td className="px-4 py-3 text-white/60 text-sm">{m.etapa || '-'}</td><td className="px-4 py-3 text-white/80 text-sm max-w-xs">{m.descricao}</td><td className="px-4 py-3 text-white/80 text-sm">{fmtBRL(m.valor_bruto)}</td><td className="px-4 py-3 text-white/60 text-sm">{fmtBRL(m.retencao_valor)}</td><td className="px-4 py-3 text-white/80 text-sm">{fmtBRL(m.valor_liquido)}</td><td className="px-4 py-3 text-xs"><span className={m.status === 'aprovada' ? 'text-emerald-300' : m.status === 'cancelada' ? 'text-rose-300' : 'text-amber-200'}>{m.status}</span></td><td className="px-4 py-3 text-xs">{m.conta_pagar ? <span className={m.conta_pagar.status === 'pago' ? 'text-emerald-300' : m.conta_pagar.status === 'pendente' ? 'text-amber-200' : 'text-white/45'}>{m.conta_pagar.status}{m.conta_pagar.pagamento ? ` · ${fmtDate(m.conta_pagar.pagamento)}` : ''}</span> : <span className="text-white/35">sem título</span>}</td><td className="px-4 py-3 text-sm">{m.status === 'rascunho' ? <button onClick={() => approveMedicao(m)} disabled={saving} className="text-emerald-300 hover:text-emerald-200 disabled:opacity-40">Aprovar</button> : m.movimentacao_id ? <span className="text-white/45">Conta criada</span> : <button onClick={() => openMedicaoContaPagar(m)} disabled={saving} className="text-indigo-200 hover:text-indigo-100 disabled:opacity-40">Gerar conta</button>}</td></tr>)}
	                      {!(contratoDossie.medicoes || []).length && <tr><td colSpan={9} className="px-4 py-8 text-center text-white/35 text-sm">Nenhuma medição registrada.</td></tr>}
	                    </tbody></table></div>
	                  </GlassCard>

	                  <GlassCard className="overflow-hidden lg:col-span-2">
	                    <table className="w-full">
	                      <thead>
	                        <tr className="border-b border-white/10">
	                          {['Trabalhador', 'Função', 'Salário carteira', 'Status', 'Pendências'].map(h => (
	                            <th key={h} className="text-left text-white/50 text-xs px-4 py-3 font-medium">{h}</th>
	                          ))}
	                        </tr>
	                      </thead>
	                      <tbody>
	                        {(contratoDossie.trabalhadores || []).map(t => {
	                          const comp = (contratoDossie.compliance.trabalhadores || []).find(r => r.trabalhador_id === t.id);
	                          return (
	                            <tr key={t.id} className="border-b border-white/5">
	                              <td className="px-4 py-3 text-white/80 text-sm">{t.nome}</td>
	                              <td className="px-4 py-3 text-white/60 text-sm">{t.funcao || '-'}</td>
	                              <td className="px-4 py-3 text-white/60 text-sm">{fmtBRL(t.salario_carteira)}</td>
	                              <td className="px-4 py-3 text-white/50 text-sm">{t.status}</td>
	                              <td className="px-4 py-3 text-white/50 text-xs">{comp?.pendencias?.join(', ') || 'OK'}</td>
	                            </tr>
	                          );
	                        })}
	                        {(contratoDossie.trabalhadores || []).length === 0 && (
	                          <tr><td colSpan={5} className="px-4 py-8 text-center text-white/30">Sem trabalhadores vinculados</td></tr>
	                        )}
	                      </tbody>
	                    </table>
	                  </GlassCard>

	                  <GlassCard className="p-5">
	                    <p className="text-white/50 text-sm mb-3">Documentos</p>
	                    <div className="space-y-2">
		                      {(contratoDossie.documentos || []).slice(0, 10).map(doc => (
		                        <div key={doc.id} className="flex items-center justify-between gap-3 border-b border-white/5 pb-2">
		                          <div>
		                            <p className="text-white/80 text-sm">{doc.nome_original}</p>
		                            <p className="text-white/40 text-xs">{doc.tipo} · {doc.competencia || '-'} · {doc.trabalhador || 'contrato'}</p>
		                          </div>
		                          <div className="flex items-center gap-1">
		                            <button title="Abrir" onClick={() => previewDoc(empreiteiraDocUrl(doc.id), doc)} className="rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white">
		                              <Eye className="h-4 w-4" />
		                            </button>
		                            <button title="Baixar" onClick={() => downloadDoc(empreiteiraDocUrl(doc.id), doc)} className="rounded-lg p-2 text-white/60 hover:bg-white/10 hover:text-white">
		                              <Download className="h-4 w-4" />
		                            </button>
		                          </div>
		                        </div>
		                      ))}
	                      {(contratoDossie.documentos || []).length === 0 && <p className="text-white/35 text-sm">Sem documentos anexados</p>}
	                    </div>
	                  </GlassCard>
	                </div>
	              ) : (
	                <GlassCard className="p-8 text-center text-white/35">Cadastre ou selecione um contrato de empreiteira</GlassCard>
	              )}
	            </div>
	          )}
	        </FadeIn>
	      )}

      {/* Modal Novo Lançamento */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold text-lg">Novo Lançamento</h2>
              <button onClick={() => { setShowModal(false); setError(''); }} className="text-white/40 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-white/50 text-xs mb-1 block">Funcionário *</label>
                <select
                  value={form.funcionario_id}
                  onChange={e => setForm(f => ({ ...f, funcionario_id: e.target.value }))}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">Selecionar...</option>
                  {funcionarios.map(f => (
                    <option key={f.id} value={f.id}>{f.nome} — {f.funcao || f.cargo} ({f.empresa})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Tipo *</label>
                  <select
                    value={form.tipo}
                    onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Forma Pagamento</label>
                  <select
                    value={form.forma_pagamento}
                    onChange={e => setForm(f => ({ ...f, forma_pagamento: e.target.value }))}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    {FORMAS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-white/50 text-xs mb-1 block">Valor (R$) *</label>
                <GlassInput
                  type="number"
                  value={form.valor}
                  onChange={e => setForm(f => ({ ...f, valor: e.target.value }))}
                  placeholder="0,00"
                  step="0.01"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Data Pagamento *</label>
                  <input
                    type="date"
                    value={form.data_pagamento}
                    onChange={e => setForm(f => ({ ...f, data_pagamento: e.target.value }))}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Mês Referência *</label>
                  <input
                    type="month"
                    value={form.mes_referencia}
                    onChange={e => setForm(f => ({ ...f, mes_referencia: e.target.value }))}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-white/50 text-xs mb-1 block">Observação</label>
                <textarea
                  value={form.observacao}
                  onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))}
                  rows={2}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none"
                  placeholder="Opcional..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowModal(false); setError(''); }}
                className="flex-1 px-4 py-2 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 text-sm"
              >
                Cancelar
              </button>
              <GlassButton onClick={saveLancamento} disabled={saving} className="flex-1 flex items-center justify-center gap-2">
                {saving ? <Loader size={14} className="animate-spin" /> : null}
                {saving ? 'Salvando...' : 'Salvar'}
              </GlassButton>
            </div>
          </div>
        </div>
      )}

      {/* Modal Novo Funcionário */}
      {showFuncionarioModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold text-lg">{funcForm.id ? 'Editar funcionário' : 'Novo funcionário'}</h2>
              <button onClick={() => { setShowFuncionarioModal(false); setError(''); }} className="text-white/40 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-white/50 text-xs mb-1 block">Nome *</label>
                <GlassInput value={funcForm.nome} onChange={e => setFuncForm(f => ({ ...f, nome: e.target.value }))} placeholder="Nome completo" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Função</label>
                  <GlassInput value={funcForm.funcao} onChange={e => setFuncForm(f => ({ ...f, funcao: e.target.value }))} placeholder="Pedreiro, mestre..." />
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Salário Base</label>
                  <GlassInput type="number" step="0.01" value={funcForm.salario_base} onChange={e => setFuncForm(f => ({ ...f, salario_base: e.target.value }))} placeholder="0,00" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Empresa</label>
                  <select value={funcForm.empresa} onChange={e => setFuncForm(f => ({ ...f, empresa: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="MMC">MMC</option>
                    <option value="MMI">MMI</option>
                  </select>
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Regime</label>
                  <select value={funcForm.regime} onChange={e => setFuncForm(f => ({ ...f, regime: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="pendente_documentacao">Documentação do vínculo pendente</option>
                    <option value="registrado">Vínculo registrado</option>
                    <option value="autonomo">Autônomo / validar documentação</option>
                    <option value="pj">Prestador PJ</option>
                  </select>
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Status</label>
                  <select value={funcForm.status} onChange={e => setFuncForm(f => ({ ...f, status: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="ativo">Ativo</option>
                    <option value="ferias">Férias</option>
                    <option value="afastado">Afastado</option>
                    <option value="demitido">Demitido</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Admissão *</label>
                  <input type="date" value={funcForm.data_admissao} onChange={e => setFuncForm(f => ({ ...f, data_admissao: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Telefone</label>
                  <GlassInput value={funcForm.telefone} onChange={e => setFuncForm(f => ({ ...f, telefone: e.target.value }))} placeholder="(00) 00000-0000" />
                </div>
              </div>

              <div>
                <label className="text-white/50 text-xs mb-1 block">CPF</label>
                <GlassInput value={funcForm.cpf} onChange={e => setFuncForm(f => ({ ...f, cpf: e.target.value }))} placeholder="Opcional" />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowFuncionarioModal(false); setError(''); }} className="flex-1 px-4 py-2 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 text-sm">
                Cancelar
              </button>
              <GlassButton onClick={saveFuncionario} disabled={saving} className="flex-1 flex items-center justify-center gap-2">
                {saving ? <Loader size={14} className="animate-spin" /> : null}
                {saving ? 'Salvando...' : 'Salvar'}
              </GlassButton>
            </div>
          </div>
        </div>
      )}

      {/* Modais Dossiê */}
      {dossieModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold text-lg">
                {dossieModal === 'documento' && 'Anexar Documento'}
                {dossieModal === 'obra' && 'Vincular Obra'}
                {dossieModal === 'ferias' && 'Registrar Férias'}
                {dossieModal === 'encargo' && 'Configurar Encargo'}
                {dossieModal === 'ponto' && 'Registrar Ponto'}
              </h2>
              <button onClick={closeDossieModal} className="text-white/40 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>
            )}

            {dossieModal === 'documento' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Tipo</label>
                    <select value={docForm.tipo} onChange={e => setDocForm(f => ({ ...f, tipo: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="documento">Documento</option>
                      <option value="atestado">Atestado</option>
                      <option value="cartao_ponto">Cartão ponto</option>
                      <option value="contrato">Contrato</option>
                      <option value="exame">Exame</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Data</label>
                    <input type="date" value={docForm.data_documento} onChange={e => setDocForm(f => ({ ...f, data_documento: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Arquivo *</label>
                  <input type="file" onChange={e => setDocForm(f => ({ ...f, file: e.target.files?.[0] || null }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Observação</label>
                  <textarea value={docForm.observacao} onChange={e => setDocForm(f => ({ ...f, observacao: e.target.value }))} rows={2} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none" />
                </div>
                {docForm.tipo === 'cartao_ponto' && (
                  <p className="text-white/40 text-xs">O arquivo fica salvo para revisão/OCR. Dias trabalhados só são criados automaticamente quando o bot enviar dados estruturados.</p>
                )}
              </div>
            )}

            {dossieModal === 'obra' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Obra *</label>
                    <select value={obraForm.obra_id} onChange={e => setObraForm(f => ({ ...f, obra_id: e.target.value, etapa_id: '' }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Selecionar...</option>
                      {obras.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Etapa</label>
                    <select value={obraForm.etapa_id} onChange={e => setObraForm(f => ({ ...f, etapa_id: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Sem etapa</option>
                      {etapas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Função na obra</label>
                    <GlassInput value={obraForm.funcao_obra} onChange={e => setObraForm(f => ({ ...f, funcao_obra: e.target.value }))} placeholder="Opcional" />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Início</label>
                    <input type="date" value={obraForm.data_inicio} onChange={e => setObraForm(f => ({ ...f, data_inicio: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Status</label>
                    <select value={obraForm.status} onChange={e => setObraForm(f => ({ ...f, status: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="ativo">Ativo</option>
                      <option value="encerrado">Encerrado</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Observação</label>
                  <textarea value={obraForm.observacao} onChange={e => setObraForm(f => ({ ...f, observacao: e.target.value }))} rows={2} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none" />
                </div>
              </div>
            )}

            {dossieModal === 'ferias' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Período início *</label>
                    <input type="date" value={feriasForm.periodo_aquisitivo_inicio} onChange={e => setFeriasForm(f => ({ ...f, periodo_aquisitivo_inicio: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Período fim *</label>
                    <input type="date" value={feriasForm.periodo_aquisitivo_fim} onChange={e => setFeriasForm(f => ({ ...f, periodo_aquisitivo_fim: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Direito</label>
                    <GlassInput type="number" value={feriasForm.dias_direito} onChange={e => setFeriasForm(f => ({ ...f, dias_direito: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Gozados</label>
                    <GlassInput type="number" value={feriasForm.dias_gozados} onChange={e => setFeriasForm(f => ({ ...f, dias_gozados: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Status</label>
                    <select value={feriasForm.status} onChange={e => setFeriasForm(f => ({ ...f, status: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="aberta">Aberta</option>
                      <option value="programada">Programada</option>
                      <option value="gozada">Gozada</option>
                      <option value="paga">Paga</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Gozo início</label>
                    <input type="date" value={feriasForm.data_inicio_gozo} onChange={e => setFeriasForm(f => ({ ...f, data_inicio_gozo: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Gozo fim</label>
                    <input type="date" value={feriasForm.data_fim_gozo} onChange={e => setFeriasForm(f => ({ ...f, data_fim_gozo: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                </div>
                <textarea value={feriasForm.observacao} onChange={e => setFeriasForm(f => ({ ...f, observacao: e.target.value }))} rows={2} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none" placeholder="Observação" />
              </div>
            )}

            {dossieModal === 'encargo' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Tipo *</label>
                    <GlassInput value={encargoForm.tipo} onChange={e => setEncargoForm(f => ({ ...f, tipo: e.target.value }))} placeholder="INSS, FGTS, IRRF..." />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Modo</label>
                    <select value={encargoForm.modo} onChange={e => setEncargoForm(f => ({ ...f, modo: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="percentual">Percentual</option>
                      <option value="fixo">Valor fixo</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Valor *</label>
                    <GlassInput type="number" step="0.01" value={encargoForm.valor} onChange={e => setEncargoForm(f => ({ ...f, valor: e.target.value }))} placeholder={encargoForm.modo === 'percentual' ? 'Ex: 8' : '0,00'} />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Base</label>
                    <select value={encargoForm.base} onChange={e => setEncargoForm(f => ({ ...f, base: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="salario_base">Salário base</option>
                      <option value="valor_fixo">Valor fixo</option>
                    </select>
                  </div>
                </div>
                <textarea value={encargoForm.observacao} onChange={e => setEncargoForm(f => ({ ...f, observacao: e.target.value }))} rows={2} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none" placeholder="Fonte do percentual/valor, orientação do contador..." />
              </div>
            )}

            {dossieModal === 'ponto' && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Data *</label>
                    <input type="date" value={pontoForm.data} onChange={e => setPontoForm(f => ({ ...f, data: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Tipo</label>
                    <select value={pontoForm.tipo_dia} onChange={e => setPontoForm(f => ({ ...f, tipo_dia: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="trabalhado">Trabalhado</option>
                      <option value="falta">Falta</option>
                      <option value="atestado">Atestado</option>
                      <option value="feriado">Feriado</option>
                      <option value="folga">Folga</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Horas</label>
                    <GlassInput type="number" step="0.5" value={pontoForm.horas} onChange={e => setPontoForm(f => ({ ...f, horas: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Obra</label>
                    <select value={pontoForm.obra_id} onChange={e => setPontoForm(f => ({ ...f, obra_id: e.target.value, etapa_id: '' }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Sem obra</option>
                      {obras.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Etapa</label>
                    <select value={pontoForm.etapa_id} onChange={e => setPontoForm(f => ({ ...f, etapa_id: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Sem etapa</option>
                      {etapas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                    </select>
                  </div>
                </div>
                <textarea value={pontoForm.observacao} onChange={e => setPontoForm(f => ({ ...f, observacao: e.target.value }))} rows={2} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none" placeholder="Observação" />
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button onClick={closeDossieModal} className="flex-1 px-4 py-2 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 text-sm">
                Cancelar
              </button>
              <GlassButton
                onClick={
                  dossieModal === 'documento' ? saveDocumento :
                  dossieModal === 'obra' ? saveObraVinculo :
                  dossieModal === 'ferias' ? saveFerias :
                  dossieModal === 'encargo' ? saveEncargo :
                  savePonto
                }
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2"
              >
                {saving ? <Loader size={14} className="animate-spin" /> : null}
                {saving ? 'Salvando...' : 'Salvar'}
              </GlassButton>
            </div>
          </div>
        </div>
      )}

      {/* Modais Empreiteiras */}
      {empreiteiraModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold text-lg">
                {empreiteiraModal === 'empreiteira' && 'Nova Empreiteira'}
                {empreiteiraModal === 'contrato' && 'Contrato de Empreiteira'}
                {empreiteiraModal === 'trabalhador' && 'Trabalhador Terceirizado'}
                {empreiteiraModal === 'documento' && 'Documento de Conformidade'}
                {empreiteiraModal === 'medicao' && 'Nova medição de empreiteira'}
                {empreiteiraModal === 'conta-pagar-medicao' && 'Gerar conta a pagar'}
              </h2>
              <button onClick={closeEmpreiteiraModal} className="text-white/40 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>
            )}

            {empreiteiraModal === 'empreiteira' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Nome *</label>
                    <GlassInput value={empreiteiraForm.nome} onChange={e => setEmpreiteiraForm(f => ({ ...f, nome: e.target.value }))} placeholder="Nome fantasia" />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">CNPJ</label>
                    <GlassInput value={empreiteiraForm.cnpj} onChange={e => setEmpreiteiraForm(f => ({ ...f, cnpj: e.target.value }))} placeholder="00.000.000/0000-00" />
                  </div>
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Razão social</label>
                  <GlassInput value={empreiteiraForm.razao_social} onChange={e => setEmpreiteiraForm(f => ({ ...f, razao_social: e.target.value }))} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <GlassInput value={empreiteiraForm.responsavel} onChange={e => setEmpreiteiraForm(f => ({ ...f, responsavel: e.target.value }))} placeholder="Responsável" />
                  <GlassInput value={empreiteiraForm.telefone} onChange={e => setEmpreiteiraForm(f => ({ ...f, telefone: e.target.value }))} placeholder="Telefone" />
                  <GlassInput value={empreiteiraForm.email} onChange={e => setEmpreiteiraForm(f => ({ ...f, email: e.target.value }))} placeholder="E-mail" />
                </div>
                <textarea value={empreiteiraForm.observacao} onChange={e => setEmpreiteiraForm(f => ({ ...f, observacao: e.target.value }))} rows={2} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none" placeholder="Observação" />
              </div>
            )}

            {empreiteiraModal === 'contrato' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Empreiteira *</label>
                    <select value={contratoForm.empreiteira_id} onChange={e => setContratoForm(f => ({ ...f, empreiteira_id: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Selecionar...</option>
                      {empreiteiras.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Obra *</label>
                    <select value={contratoForm.obra_id} onChange={e => setContratoForm(f => ({ ...f, obra_id: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Selecionar...</option>
                      {obras.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <GlassInput value={contratoForm.numero_contrato} onChange={e => setContratoForm(f => ({ ...f, numero_contrato: e.target.value }))} placeholder="Número do contrato" />
                  <GlassInput value={contratoForm.cno} onChange={e => setContratoForm(f => ({ ...f, cno: e.target.value }))} placeholder="CNO da obra" />
                </div>
                <GlassInput value={contratoForm.objeto} onChange={e => setContratoForm(f => ({ ...f, objeto: e.target.value }))} placeholder="Objeto do contrato" />
                <div className="grid grid-cols-4 gap-3">
                  <input type="date" value={contratoForm.data_inicio} onChange={e => setContratoForm(f => ({ ...f, data_inicio: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                  <input type="date" value={contratoForm.data_fim} onChange={e => setContratoForm(f => ({ ...f, data_fim: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                  <GlassInput type="number" step="0.01" value={contratoForm.valor_contrato} onChange={e => setContratoForm(f => ({ ...f, valor_contrato: e.target.value }))} placeholder="Valor" />
                  <GlassInput type="number" step="0.01" value={contratoForm.retencao_percentual} onChange={e => setContratoForm(f => ({ ...f, retencao_percentual: e.target.value }))} placeholder="Retenção %" />
                </div>
                <textarea value={contratoForm.observacao} onChange={e => setContratoForm(f => ({ ...f, observacao: e.target.value }))} rows={2} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none" placeholder="Observação" />
              </div>
            )}

            {empreiteiraModal === 'trabalhador' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <GlassInput value={terceiroForm.nome} onChange={e => setTerceiroForm(f => ({ ...f, nome: e.target.value }))} placeholder="Nome completo *" />
                  <GlassInput value={terceiroForm.cpf} onChange={e => setTerceiroForm(f => ({ ...f, cpf: e.target.value }))} placeholder="CPF" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <GlassInput value={terceiroForm.funcao} onChange={e => setTerceiroForm(f => ({ ...f, funcao: e.target.value }))} placeholder="Função" />
                  <GlassInput type="number" step="0.01" value={terceiroForm.salario_carteira} onChange={e => setTerceiroForm(f => ({ ...f, salario_carteira: e.target.value }))} placeholder="Salário carteira" />
                  <input type="date" value={terceiroForm.data_inicio} onChange={e => setTerceiroForm(f => ({ ...f, data_inicio: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <textarea value={terceiroForm.observacao} onChange={e => setTerceiroForm(f => ({ ...f, observacao: e.target.value }))} rows={2} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none" placeholder="Observação" />
              </div>
            )}

            {empreiteiraModal === 'documento' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Tipo *</label>
                    <select value={empDocForm.tipo} onChange={e => setEmpDocForm(f => ({ ...f, tipo: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="comprovante_salario">Comprovante salário</option>
                      <option value="fgts">FGTS</option>
                      <option value="gfip">GFIP</option>
                      <option value="dds">DDS mensal</option>
                      <option value="registro">Folha/registro</option>
                      <option value="nr">NR</option>
                      <option value="exame_medico">Exame médico</option>
                      <option value="contrato">Contrato</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Competência</label>
                    <input type="month" value={empDocForm.competencia} onChange={e => setEmpDocForm(f => ({ ...f, competencia: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs mb-1 block">Trabalhador</label>
                    <select value={empDocForm.trabalhador_id} onChange={e => setEmpDocForm(f => ({ ...f, trabalhador_id: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Documento do contrato/competência</option>
                      {(contratoDossie?.trabalhadores || []).map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                    </select>
                  </div>
                  <GlassInput type="number" step="0.01" value={empDocForm.valor_deposito} onChange={e => setEmpDocForm(f => ({ ...f, valor_deposito: e.target.value }))} placeholder="Valor depósito" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input type="date" value={empDocForm.data_documento} onChange={e => setEmpDocForm(f => ({ ...f, data_documento: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                  <input type="file" onChange={e => setEmpDocForm(f => ({ ...f, file: e.target.files?.[0] || null }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <textarea value={empDocForm.observacao} onChange={e => setEmpDocForm(f => ({ ...f, observacao: e.target.value }))} rows={2} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none" placeholder="Observação" />
              </div>
            )}

            {empreiteiraModal === 'medicao' && (
              <div className="space-y-4">
                <p className="text-sm text-white/55">A medição entra como rascunho. A aprovação é uma conferência interna e não cria uma conta a pagar.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-white/50 text-xs mb-1 block">Competência *</label><input type="month" value={medicaoForm.competencia} onChange={e => setMedicaoForm(f => ({ ...f, competencia: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" /></div>
                  <div><label className="text-white/50 text-xs mb-1 block">Etapa da obra</label><select value={medicaoForm.etapa_id} onChange={e => setMedicaoForm(f => ({ ...f, etapa_id: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"><option value="">Sem etapa formal</option>{etapas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}</select></div>
                </div>
                <div className="grid grid-cols-2 gap-3"><input type="date" value={medicaoForm.periodo_inicio} onChange={e => setMedicaoForm(f => ({ ...f, periodo_inicio: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" /><input type="date" value={medicaoForm.periodo_fim} onChange={e => setMedicaoForm(f => ({ ...f, periodo_fim: e.target.value }))} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" /></div>
                <textarea value={medicaoForm.descricao} onChange={e => setMedicaoForm(f => ({ ...f, descricao: e.target.value }))} rows={3} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none" placeholder="Serviço medido *" />
                <div className="grid grid-cols-3 gap-3"><GlassInput type="number" step="0.001" value={medicaoForm.percentual_executado} onChange={e => setMedicaoForm(f => ({ ...f, percentual_executado: e.target.value }))} placeholder="% executado" /><GlassInput type="number" step="0.01" value={medicaoForm.valor_bruto} onChange={e => setMedicaoForm(f => ({ ...f, valor_bruto: e.target.value }))} placeholder="Valor bruto *" /><GlassInput type="number" step="0.01" value={medicaoForm.retencao_valor} onChange={e => setMedicaoForm(f => ({ ...f, retencao_valor: e.target.value }))} placeholder="Retenção" /></div>
                <textarea value={medicaoForm.observacao} onChange={e => setMedicaoForm(f => ({ ...f, observacao: e.target.value }))} rows={2} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm resize-none" placeholder="Evidências e observações" />
              </div>
            )}

            {empreiteiraModal === 'conta-pagar-medicao' && (
              <div className="space-y-4">
                <div className="rounded-lg border border-indigo-400/20 bg-indigo-500/10 p-4 text-sm">
                  <p className="font-medium text-indigo-100">Medição aprovada: {medicaoContaPagar?.competencia}</p>
                  <p className="mt-1 text-white/65">{medicaoContaPagar?.descricao}</p>
                  <p className="mt-3 text-white/90">Valor líquido: {fmtBRL(medicaoContaPagar?.valor_liquido)}</p>
                </div>
                <div><label className="text-white/50 text-xs mb-1 block">Vencimento *</label><input type="date" value={vencimentoMedicao} onChange={e => setVencimentoMedicao(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" /></div>
                <p className="text-xs text-white/45">Esta ação cria uma única conta a pagar pendente no Financeiro. Ela não baixa nem paga o título.</p>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button onClick={closeEmpreiteiraModal} className="flex-1 px-4 py-2 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 text-sm">
                Cancelar
              </button>
              <GlassButton
                onClick={
                  empreiteiraModal === 'empreiteira' ? saveEmpreiteira :
                  empreiteiraModal === 'contrato' ? saveContratoEmpreiteira :
                  empreiteiraModal === 'trabalhador' ? saveTerceiro :
                  empreiteiraModal === 'documento' ? saveEmpDocumento :
                  empreiteiraModal === 'medicao' ? saveMedicao :
                  createMedicaoContaPagar
                }
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2"
              >
                {saving ? <Loader size={14} className="animate-spin" /> : null}
                {saving ? 'Salvando...' : empreiteiraModal === 'conta-pagar-medicao' ? 'Criar conta pendente' : 'Salvar'}
              </GlassButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RH;
